import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query'

import { apiClients, makeClientQueryFn } from './lib'

import { mapKeys } from 'lodash-es'

import { AxiosResponse } from 'axios'

import {
  InsightImportance,
  HealthType,
  InsightOutputUnit,
  MetricExample,
  Nullable,
  PlatformUnitParams,
  ReportParams,
  ReportProvider,
  ReportProviderV2,
  SeriesParams,
  TimeseriesGranularity,
} from '@percept/types'

import { flatten, mapValues, sortBy } from 'lodash-es'

import { reportProviders } from '@percept/constants'

import { coerceReportProvider, isoDateUTC } from '@percept/utils'


export type InsightUnitsByImportance = Record<InsightImportance, InsightOutputUnit[]>

type LegacyInsightUnitsByImportance = Record<string, InsightOutputUnit[]>

const insightsByImportanceAdapter = (
  response: AxiosResponse<LegacyInsightUnitsByImportance>
): InsightUnitsByImportance => {
  // Handle legacy values from API
  const translationMap: Record<string, InsightImportance> = {
    'PRIORITY_WITH_WASTAGE': 'KEY_TASK',
    'PRIORITY_WITHOUT_WASTAGE': 'SECONDARY_TASK',
  }
  return mapKeys(response.data, (v, k) => translationMap[k] || k) as InsightUnitsByImportance
}


export type CategoryScoreMapping = Record<string, HealthType>

export const useInsightUnitsByImportance = ({
  series_id,
  report_id,
  result_id,
  ...options
}: (
  Nullable<SeriesParams & ReportParams>
  & Partial<Nullable<{ result_id: string }>>
  & Partial<UseQueryOptions<InsightUnitsByImportance>>
)): UseQueryResult<InsightUnitsByImportance> => {
  return useQuery({
    queryKey: ['insightsByImportance', report_id, result_id],
    queryFn: makeClientQueryFn<LegacyInsightUnitsByImportance, InsightUnitsByImportance>(
      apiClients.core,
      {
        url: `/report-series/${series_id}/reports/${report_id}/insight-units-by-importance`,
        params: {
          ...(result_id && { result_id }),
        },
      },
      insightsByImportanceAdapter,
    ),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: Infinity,
    enabled: !!(options.enabled !== false && series_id && report_id),
    ...options,
  })
}

export const useInsightUnitsOverallScore = ({
  series_id,
  report_id,
  result_id,
  ...options
}: (
  Nullable<SeriesParams & ReportParams>
  & Partial<Nullable<{ result_id: string }>>
  & Partial<UseQueryOptions<number | null>>
)): UseQueryResult<number | null> => {
  return useQuery({
    queryKey: ['insightUnitsOverallScore', report_id, result_id],
    queryFn: makeClientQueryFn(
      apiClients.core,
      {
        url: `/report-series/${series_id}/reports/${report_id}/insight-units-score`,
        params: {
          ...(result_id && { result_id }),
        },
      }
    ),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: Infinity,
    enabled: !!(options.enabled !== false && series_id && report_id),
    ...options,
  })
}


type ReportSeriesCategoryScoreResponse = {
  report_id: string
  category_scores: CategoryScoreMapping
}[]

export type ReportSeriesCategoryScoreMapping = Record<string, CategoryScoreMapping>

export const useReportSeriesInsightUnitScores = ({
  series_id,
  enabled,
  ...options
}: (
  Nullable<SeriesParams>
  & Partial<UseQueryOptions<ReportSeriesCategoryScoreMapping>>
)): UseQueryResult<ReportSeriesCategoryScoreMapping> => {
  return useQuery({
    queryKey: ['reportSeriesInsightUnitScores', series_id],
    queryFn: makeClientQueryFn<ReportSeriesCategoryScoreResponse, ReportSeriesCategoryScoreMapping>(
      apiClients.core,
      {
        url: `/report-series/${series_id}/insight-unit-scores`,
      },
      response => (
        response.data.reduce( (acc, { report_id, category_scores }) => {
          acc[report_id] = category_scores
          return acc
        }, {} as ReportSeriesCategoryScoreMapping)
      ),
    ),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: Infinity,
    enabled: !!(enabled !== false && series_id),
    ...options,
  })
}


type ProviderCategoryScoreTimeseriesDatumResponse = {
  source_provider: ReportProviderV2
  category: string
  health: HealthType
  start_date: string
  end_date: string
}

export type TimeseriesHealthDatum = {
  start: Date
  end: Date
  health: HealthType
}

export type ProviderCategoryScoreTimeseriesDatum = {
  provider: ReportProviderV2
  timeseriesByCategory: Record<string, TimeseriesHealthDatum[]>
}


const getCategoryScoreDateParams = ({
  start,
  end,
}: Partial<Nullable<Record<'start' | 'end', Date | null>>>): Record<'start_date' | 'reference_date', string | null> => {
  return {
    start_date: start ? isoDateUTC(start) : null,
    reference_date: end ? isoDateUTC(end) : null,
  }
}


export const useOrgUnitCategoryScores = ({
  org_unit_id,
  start,
  end,
  granularity = 'MONTH',
  enabled,
  ...options
}: (
  Nullable<PlatformUnitParams> & {
    granularity?: TimeseriesGranularity
    start?: Date | null
    end?: Date | null
  }
  & Partial<UseQueryOptions<ProviderCategoryScoreTimeseriesDatum[]>>
)): UseQueryResult<ProviderCategoryScoreTimeseriesDatum[]> => {
  const { start_date, reference_date } = getCategoryScoreDateParams({ start, end })
  return useQuery<ProviderCategoryScoreTimeseriesDatum[]>({
    queryKey: ['orgUnitCategoryScores', org_unit_id, start_date, reference_date, granularity],
    queryFn: makeClientQueryFn<ProviderCategoryScoreTimeseriesDatumResponse[], ProviderCategoryScoreTimeseriesDatum[]>(
      apiClients.core,
      {
        url: `/org-units/${org_unit_id}/insight-units/category-scores`,
        params: {
          granularity,
          start_date,
          reference_date,
        },
      },
      response => {
        const mapping = response.data.reduce( (acc, { source_provider, start_date, end_date, category, health }) => {
          acc[source_provider] = acc[source_provider] || {}
          acc[source_provider][category] = acc[source_provider][category] || []
          acc[source_provider][category].push({
            start: new Date(start_date),
            end: new Date(end_date),
            health,
          })
          return acc
        }, {} as Record<
          ReportProviderV2, Record<
            string, TimeseriesHealthDatum[]
          >
        >)

        return sortBy(
          Object.entries(mapping).map( ([provider, categoryMapping]) => {
            return {
              provider: provider as ReportProviderV2,
              timeseriesByCategory: mapValues(
                categoryMapping,
                m => sortBy(m, d => d.start.getTime())
              )
            }
          }) as ProviderCategoryScoreTimeseriesDatum[],
          d => reportProviders.indexOf(coerceReportProvider(d.provider) as ReportProvider),
        )
      }
    ),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: 1000 * 60 * 60,
    enabled: !!(enabled !== false && org_unit_id),
    ...options,
  })
}


type OrgUnitProviderCategoryScoreTimeseriesDatumResponse = ProviderCategoryScoreTimeseriesDatumResponse & {
  org_unit_id: string
  org_unit_name: string
}


export type OrgUnitProviderCategoryScoreTimeseriesDatum = ProviderCategoryScoreTimeseriesDatum & {
  org_unit_id: string
  org_unit_name: string
}


export const useChildOrgUnitCategoryScores = ({
  org_unit_id,
  start,
  end,
  granularity = 'MONTH',
  enabled,
  ...options
}: (
  Nullable<PlatformUnitParams> & {
    granularity?: TimeseriesGranularity
    start?: Date | null
    end?: Date | null
  }
  & Partial<UseQueryOptions<OrgUnitProviderCategoryScoreTimeseriesDatum[]>>
)): UseQueryResult<OrgUnitProviderCategoryScoreTimeseriesDatum[]> => {
  const { start_date, reference_date } = getCategoryScoreDateParams({ start, end })
  return useQuery<OrgUnitProviderCategoryScoreTimeseriesDatum[]>({
    queryKey: ['childOrgUnitCategoryScores', org_unit_id, start_date, reference_date, granularity],
    queryFn: makeClientQueryFn<OrgUnitProviderCategoryScoreTimeseriesDatumResponse[], OrgUnitProviderCategoryScoreTimeseriesDatum[]>(
      apiClients.core,
      {
        url: `/org-units/${org_unit_id}/insight-units/category-scores/by-child-org-unit`,
        params: {
          granularity,
          start_date,
          reference_date,
        },
      },
      response => {
        const orgIdNameMapping: Record<string, string> = {}
        const mapping = response.data.reduce(
          (acc, { org_unit_id, org_unit_name, source_provider, start_date, end_date, category, health }) => {
            // Populate the mapping
            orgIdNameMapping[org_unit_id] = org_unit_name
            acc[org_unit_id] = acc[org_unit_id] || {}
            acc[org_unit_id][source_provider] = acc[org_unit_id][source_provider] || {}
            acc[org_unit_id][source_provider][category] = acc[org_unit_id][source_provider][category] || []
            acc[org_unit_id][source_provider][category].push({
              start: new Date(start_date),
              end: new Date(end_date),
              health,
            })
            return acc
          },
          {} as Record<
          string, Record<
            ReportProviderV2, Record<
              string, TimeseriesHealthDatum[]
            >
          >
        >)

        return sortBy(
          flatten(
            Object.entries(mapping).map( ([org_unit_id, providerCategoryMapping]) => {
              return Object.entries(providerCategoryMapping).reduce( (acc, [provider, categoryMapping]) => {
                acc.push({
                  org_unit_id,
                  org_unit_name: orgIdNameMapping[org_unit_id],
                  provider: provider as ReportProviderV2,
                  timeseriesByCategory: mapValues(
                    categoryMapping,
                    m => sortBy(m, d => d.start.getTime())
                  )
                })
                return acc
              }, [] as OrgUnitProviderCategoryScoreTimeseriesDatum[])
            })
          ),
          d => reportProviders.indexOf(coerceReportProvider(d.provider) as ReportProvider),
        )
      }
    ),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: 1000 * 60 * 60,
    enabled: !!(enabled !== false && org_unit_id),
    ...options,
  })
}


export const useLatestInsightUnitsByImportance = ({
  series_id,
  enabled,
  ...options
}: (
  Nullable<SeriesParams>
  & Partial<UseQueryOptions<InsightUnitsByImportance>>
)): UseQueryResult<InsightUnitsByImportance> => {
  return useQuery({
    queryKey: ['insightsByImportance', series_id, 'latest'],
    queryFn: makeClientQueryFn<LegacyInsightUnitsByImportance, InsightUnitsByImportance>(
      apiClients.core,
      {
        url: `/report-series/${series_id}/insight-units-by-importance/latest`,
      },
      insightsByImportanceAdapter,
    ),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: Infinity,
    enabled: !!(enabled !== false && series_id),
    ...options,
  })
}


export const useInsightOutputUnitResultExamples = ({
  series_id,
  insight_output_unit_result_id,
  ...options
}: (
  SeriesParams
  & {
    insight_output_unit_result_id: string
  }
  & Partial<UseQueryOptions<MetricExample[]>>
)): UseQueryResult<MetricExample[]> => {
  return useQuery({
    queryKey: ['insightOutputUnitResults', insight_output_unit_result_id, 'entities'],
    queryFn: makeClientQueryFn<MetricExample[]>(
      apiClients.core,
      {
        url: `/report-series/${series_id}/insight-output-unit-results/${insight_output_unit_result_id}/entities`,
      }
    ),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: Infinity,
    ...options,
  })
}
